import { ref } from 'vue'
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  serverTimestamp,
  runTransaction
} from 'firebase/firestore'
import { db } from '@/services/firebase'
import { usePointSettings } from './usePointSettings'

export function useReferral() {
  const loading = ref(false)
  const error = ref(null)
  const { settings, loadSettings } = usePointSettings()

  // 고유 추천코드 생성 (6자리 영문+숫자)
  const generateReferralCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
    let code = ''
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return code
  }

  // 추천코드 중복 체크 및 고유 코드 생성
  const createUniqueReferralCode = async () => {
    let code = generateReferralCode()
    let isUnique = false
    let attempts = 0
    const maxAttempts = 10

    while (!isUnique && attempts < maxAttempts) {
      const q = query(
        collection(db, 'users'),
        where('referralCode', '==', code)
      )
      const snapshot = await getDocs(q)
      
      if (snapshot.empty) {
        isUnique = true
      } else {
        code = generateReferralCode()
        attempts++
      }
    }

    if (!isUnique) {
      throw new Error('추천코드 생성에 실패했습니다. 다시 시도해주세요.')
    }

    return code
  }

  // 추천코드로 사용자 찾기
  const findUserByReferralCode = async (referralCode) => {
    try {
      const q = query(
        collection(db, 'users'),
        where('referralCode', '==', referralCode.toUpperCase())
      )
      const snapshot = await getDocs(q)
      
      if (snapshot.empty) {
        return null
      }

      const docSnap = snapshot.docs[0]
      return { id: docSnap.id, ...docSnap.data() }
    } catch (e) {
      console.error('추천인 조회 실패:', e)
      return null
    }
  }

  // 추천코드 유효성 검증
  const validateReferralCode = async (referralCode, currentUserId = null) => {
    if (!referralCode || referralCode.trim() === '') {
      return { valid: false, message: '추천코드를 입력해주세요.' }
    }

    const code = referralCode.trim().toUpperCase()

    if (code.length !== 6) {
      return { valid: false, message: '추천코드는 6자리입니다.' }
    }

    const referrer = await findUserByReferralCode(code)

    if (!referrer) {
      return { valid: false, message: '존재하지 않는 추천코드입니다.' }
    }

    if (currentUserId && referrer.id === currentUserId) {
      return { valid: false, message: '본인의 추천코드는 사용할 수 없습니다.' }
    }

    return { 
      valid: true, 
      message: `${referrer.displayName || referrer.email}님의 추천코드입니다.`,
      referrer 
    }
  }

  // 신규 회원가입 시 처리 (추천코드 + 가입보너스)
  const processSignupWithReferral = async (userId, referralCode = null) => {
    loading.value = true
    error.value = null

    try {
      await loadSettings()
      const { signupBonus, refereeBonus, referrerBonus } = settings.value

      const myReferralCode = await createUniqueReferralCode()
      const userRef = doc(db, 'users', userId)
      
      let finalReferralCode = null

      await runTransaction(db, async (transaction) => {
        const userSnap = await transaction.get(userRef)
        
        if (!userSnap.exists()) {
          throw new Error('사용자를 찾을 수 없습니다.')
        }

        const updateData = {
          referralCode: myReferralCode,
          points: signupBonus,
          referredBy: null,
          referralCount: 0,
          slotTickets: 0,  // 신규 가입자 티켓 0장 시작
          lastAttendanceDate: null,
          updatedAt: serverTimestamp()
        }

        if (referralCode) {
          const validation = await validateReferralCode(referralCode, userId)
          
          if (validation.valid) {
            updateData.referredBy = referralCode.toUpperCase()
            finalReferralCode = referralCode.toUpperCase()
            updateData.points = signupBonus + refereeBonus
            updateData.slotTickets = 3  // 추천받은 사람도 티켓 3장!
          }
        }

        transaction.update(userRef, updateData)

        // 포인트 트랜잭션 기록 - 가입 보너스
        const signupTxRef = doc(collection(db, 'pointTransactions'))
        transaction.set(signupTxRef, {
          userId,
          type: 'earn',
          amount: signupBonus,
          balance: updateData.points,
          reason: 'signup',
          description: '회원가입 보너스',
          createdAt: serverTimestamp()
        })

        if (finalReferralCode) {
          const refereeTxRef = doc(collection(db, 'pointTransactions'))
          transaction.set(refereeTxRef, {
            userId,
            type: 'earn',
            amount: refereeBonus,
            balance: updateData.points,
            reason: 'referral_receive',
            description: '추천 가입 보너스 (+ 슬롯 티켓 3장)',
            relatedId: finalReferralCode,
            createdAt: serverTimestamp()
          })
        }
      })

      // 추천인에게 보상 지급
      if (finalReferralCode) {
        const referrer = await findUserByReferralCode(finalReferralCode)
        if (referrer) {
          await giveReferrerBonus(referrer.id, userId, referrerBonus)
        }
      }

      return { 
        success: true, 
        referralCode: myReferralCode,
        signupBonus: signupBonus,
        referralBonus: finalReferralCode ? refereeBonus : 0
      }
    } catch (e) {
      console.error('회원가입 처리 실패:', e)
      error.value = e.message || '회원가입 처리에 실패했습니다.'
      return { success: false }
    } finally {
      loading.value = false
    }
  }

  // 추천인에게 보상 지급 (포인트 + 티켓 3장)
  const giveReferrerBonus = async (referrerId, newUserId, bonusAmount = null) => {
    try {
      await loadSettings()
      const bonus = bonusAmount ?? settings.value.referrerBonus

      await runTransaction(db, async (transaction) => {
        const referrerRef = doc(db, 'users', referrerId)
        const referrerSnap = await transaction.get(referrerRef)

        if (!referrerSnap.exists()) {
          throw new Error('추천인을 찾을 수 없습니다.')
        }

        const currentPoints = referrerSnap.data().points || 0
        const currentTickets = referrerSnap.data().slotTickets || 0
        const referralCount = referrerSnap.data().referralCount || 0
        const newPoints = currentPoints + bonus

        // 추천인 포인트 + 티켓 3장 + 추천 수 업데이트
        transaction.update(referrerRef, {
          points: newPoints,
          slotTickets: currentTickets + 3,
          referralCount: referralCount + 1,
          updatedAt: serverTimestamp()
        })

        // 트랜잭션 기록
        const txRef = doc(collection(db, 'pointTransactions'))
        transaction.set(txRef, {
          userId: referrerId,
          type: 'earn',
          amount: bonus,
          balance: newPoints,
          reason: 'referral_give',
          description: '추천인 보상 (+ 슬롯 티켓 3장)',
          relatedId: newUserId,
          createdAt: serverTimestamp()
        })
      })

      return true
    } catch (e) {
      console.error('추천인 보상 지급 실패:', e)
      return false
    }
  }

  // 내 추천 현황 조회
  const getMyReferralStats = async (userId) => {
    try {
      await loadSettings()

      const userRef = doc(db, 'users', userId)
      const userSnap = await getDoc(userRef)

      if (!userSnap.exists()) {
        return null
      }

      const userData = userSnap.data()

      const q = query(
        collection(db, 'users'),
        where('referredBy', '==', userData.referralCode)
      )
      const referralsSnap = await getDocs(q)
      
      const referrals = referralsSnap.docs.map(docSnap => ({
        id: docSnap.id,
        email: docSnap.data().email,
        displayName: docSnap.data().displayName,
        createdAt: docSnap.data().createdAt
      }))

      return {
        myCode: userData.referralCode,
        referredBy: userData.referredBy,
        referralCount: userData.referralCount || 0,
        totalEarned: (userData.referralCount || 0) * settings.value.referrerBonus,
        referrals
      }
    } catch (e) {
      console.error('추천 현황 조회 실패:', e)
      return null
    }
  }

  return {
    loading,
    error,
    settings,
    generateReferralCode,
    createUniqueReferralCode,
    findUserByReferralCode,
    validateReferralCode,
    processSignupWithReferral,
    giveReferrerBonus,
    getMyReferralStats,
    loadSettings
  }
}